import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ClipboardCheck, Plus, Eye, Trash2, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const FUEL_LEVEL_LABELS = { empty: 'Empty', quarter: '¼ Tank', half: '½ Tank', three_quarter: '¾ Tank', full: 'Full' };
const INSPECTION_ITEMS = [
  { key: 'tyres', label: 'Tyres' },
  { key: 'lights', label: 'Lights (All)' },
  { key: 'oil_level', label: 'Oil Level' },
  { key: 'water_coolant', label: 'Water / Coolant' },
  { key: 'brake_fluid', label: 'Brake Fluid' },
  { key: 'wipers', label: 'Wipers' },
  { key: 'windscreen', label: 'Windscreen' },
  { key: 'body_damage', label: 'Body Damage' },
];
const defaultInspectionItem = () => ({ status: 'pass', notes: '' });

const StatusIcon = ({ status }) => {
  if (status === 'pass') return <CheckCircle2 className="w-4 h-4 text-green-500" />;
  if (status === 'fail') return <XCircle className="w-4 h-4 text-red-500" />;
  return <MinusCircle className="w-4 h-4 text-amber-500" />;
};

const VehicleInspectionsTab = ({ vehicleId, vehicle, inspections, setInspections, onRefresh }) => {
  const [inspectionDialogOpen, setInspectionDialogOpen] = useState(false);
  const [viewInspectionDialog, setViewInspectionDialog] = useState(false);
  const [deleteInspectionDialog, setDeleteInspectionDialog] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [savingInspection, setSavingInspection] = useState(false);
  const [inspectionForm, setInspectionForm] = useState({
    inspector_name: '', inspection_date: new Date().toISOString().slice(0, 10),
    odometer: '', fuel_level: 'half', general_notes: '',
    ...Object.fromEntries(INSPECTION_ITEMS.map(i => [i.key, defaultInspectionItem()])),
  });

  const updateInspItem = (key, field, value) => {
    setInspectionForm(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleSaveInspection = async () => {
    if (!inspectionForm.inspector_name || !inspectionForm.inspection_date) {
      toast.error('Inspector name and date are required');
      return;
    }
    setSavingInspection(true);
    try {
      await api.createInspection({
        vehicle_id: vehicleId,
        ...inspectionForm,
        odometer: inspectionForm.odometer ? parseFloat(inspectionForm.odometer) : null,
      });
      toast.success('Inspection saved');
      setInspectionDialogOpen(false);
      setInspectionForm({
        inspector_name: '', inspection_date: new Date().toISOString().slice(0, 10),
        odometer: '', fuel_level: 'half', general_notes: '',
        ...Object.fromEntries(INSPECTION_ITEMS.map(i => [i.key, defaultInspectionItem()])),
      });
      onRefresh();
    } catch {
      toast.error('Failed to save inspection');
    } finally {
      setSavingInspection(false);
    }
  };

  const handleDeleteInspection = async () => {
    if (!selectedInspection) return;
    try {
      await api.deleteInspection(vehicleId, selectedInspection.id);
      toast.success('Inspection deleted');
      setInspections(prev => prev.filter(i => i.id !== selectedInspection.id));
      setDeleteInspectionDialog(false);
    } catch {
      toast.error('Failed to delete inspection');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-heading font-semibold">Vehicle Inspections</CardTitle>
          <Button onClick={() => setInspectionDialogOpen(true)} className="bg-gray-800 hover:bg-gray-700" data-testid="new-inspection-btn">
            <Plus className="w-4 h-4 mr-2" />New Inspection
          </Button>
        </CardHeader>
        <CardContent>
          {inspections.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No inspections recorded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {inspections.map(insp => (
                <div key={insp.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-md hover:bg-slate-100" data-testid={`inspection-${insp.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center ${insp.overall_pass ? 'bg-green-100' : 'bg-red-100'}`}>
                      {insp.overall_pass ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{insp.inspection_date}</span>
                        <Badge className={insp.overall_pass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{insp.overall_pass ? 'Pass' : 'Fail'}</Badge>
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5">
                        Inspector: {insp.inspector_name}
                        {insp.odometer && <span className="ml-2 font-data">{insp.odometer.toLocaleString()} km</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedInspection(insp); setViewInspectionDialog(true); }} data-testid={`view-insp-${insp.id}`} title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedInspection(insp); setDeleteInspectionDialog(true); }} data-testid={`delete-insp-${insp.id}`} title="Delete"><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Inspection Dialog */}
      <Dialog open={inspectionDialogOpen} onOpenChange={setInspectionDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Vehicle Inspection</DialogTitle>
            <DialogDescription>{vehicle?.name} — {vehicle?.registration}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inspector Name *</Label>
                <Input value={inspectionForm.inspector_name} onChange={(e) => setInspectionForm(f => ({ ...f, inspector_name: e.target.value }))} placeholder="Full name" data-testid="inspector-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Inspection Date *</Label>
                <Input type="date" value={inspectionForm.inspection_date} onChange={(e) => setInspectionForm(f => ({ ...f, inspection_date: e.target.value }))} data-testid="inspection-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Odometer Reading</Label>
                <Input type="number" value={inspectionForm.odometer} onChange={(e) => setInspectionForm(f => ({ ...f, odometer: e.target.value }))} placeholder={vehicle?.usage_unit === 'hours' ? 'Hours' : 'km'} className="font-data" data-testid="inspection-odometer-input" />
              </div>
              <div className="space-y-2">
                <Label>Fuel Level</Label>
                <Select value={inspectionForm.fuel_level} onValueChange={(v) => setInspectionForm(f => ({ ...f, fuel_level: v }))}>
                  <SelectTrigger data-testid="fuel-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FUEL_LEVEL_LABELS).map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border rounded-md overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b">
                <p className="text-sm font-semibold text-gray-700">Checklist Items</p>
              </div>
              <div className="divide-y">
                {INSPECTION_ITEMS.map(item => (
                  <div key={item.key} className="px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-800">{item.label}</span>
                      <div className="flex gap-2">
                        {['pass', 'fail', 'note'].map(s => (
                          <button key={s} type="button" onClick={() => updateInspItem(item.key, 'status', s)} data-testid={`${item.key}-${s}`}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              inspectionForm[item.key]?.status === s
                                ? s === 'pass' ? 'bg-green-500 text-white' : s === 'fail' ? 'bg-red-500 text-white' : 'bg-amber-400 text-white'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}>
                            {s === 'note' ? 'Note' : s.charAt(0).toUpperCase() + s.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    {(inspectionForm[item.key]?.status === 'fail' || inspectionForm[item.key]?.status === 'note') && (
                      <Input value={inspectionForm[item.key]?.notes || ''} onChange={(e) => updateInspItem(item.key, 'notes', e.target.value)} placeholder="Add notes..." className="text-sm" data-testid={`${item.key}-notes`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>General Notes</Label>
              <Textarea value={inspectionForm.general_notes} onChange={(e) => setInspectionForm(f => ({ ...f, general_notes: e.target.value }))} placeholder="Any additional observations..." rows={2} data-testid="general-notes-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setInspectionDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveInspection} disabled={savingInspection} className="bg-gray-800 hover:bg-gray-700" data-testid="save-inspection-btn">
                <ClipboardCheck className="w-4 h-4 mr-2" />{savingInspection ? 'Saving...' : 'Save Inspection'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Inspection */}
      {selectedInspection && (
        <Dialog open={viewInspectionDialog} onOpenChange={setViewInspectionDialog}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                {selectedInspection.overall_pass ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                Inspection — {selectedInspection.inspection_date}
              </DialogTitle>
              <DialogDescription>Inspector: {selectedInspection.inspector_name}{selectedInspection.odometer ? ` | ${selectedInspection.odometer.toLocaleString()} km` : ''}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                <span className="text-sm font-medium text-gray-700">Fuel Level:</span>
                <span className="text-sm font-data">{FUEL_LEVEL_LABELS[selectedInspection.fuel_level] || selectedInspection.fuel_level}</span>
              </div>
              <div className="border rounded-md divide-y">
                {INSPECTION_ITEMS.map(item => {
                  const val = selectedInspection[item.key];
                  if (!val) return null;
                  return (
                    <div key={item.key} className="flex items-start gap-3 px-4 py-3">
                      <StatusIcon status={val.status} />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-800">{item.label}</span>
                        {val.notes && <p className="text-xs text-gray-500 mt-0.5">{val.notes}</p>}
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${val.status === 'pass' ? 'bg-green-100 text-green-700' : val.status === 'fail' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {val.status}
                      </span>
                    </div>
                  );
                })}
              </div>
              {selectedInspection.general_notes && (
                <div className="p-3 bg-gray-50 rounded-md">
                  <p className="text-xs font-medium text-gray-500 mb-1">General Notes</p>
                  <p className="text-sm text-gray-700">{selectedInspection.general_notes}</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Inspection */}
      <AlertDialog open={deleteInspectionDialog} onOpenChange={setDeleteInspectionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inspection?</AlertDialogTitle>
            <AlertDialogDescription>Delete inspection from {selectedInspection?.inspection_date}? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInspection} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VehicleInspectionsTab;
